<template>
  <div class="ur-admin">
    <div class="ur-layout">
      <!-- ===== Sidebar ===== -->
      <aside class="ur-sidebar">
        <!-- Logo 區 -->
        <div class="ur-sidebar-logo">
          <img :src="logoUrl" class="ur-sidebar-logo-img" alt="logo" />
        </div>

        <!-- 目前建案名稱 -->
        <div class="ur-sidebar-project">{{ $t('URBANRENEWAL_ADMIN.CURRENT_PROJECT') }}</div>
        <div class="ur-sidebar-project-name" :title="currentProjectName">{{ currentProjectName }}</div>
        <div class="ur-sidebar-divider"></div>

        <!-- 導覽項目 -->
        <nav class="ur-sidebar-nav">
          <button
            v-for="item in navItems"
            :key="item.key"
            class="ur-nav-item"
            :class="{ 'is-active': isNavActive(item.path) }"
            @click="navigateTo(item.path)"
          >
            <span class="ur-nav-icon" v-html="item.icon"></span>
            <span>{{ $t(item.labelKey) }}</span>
          </button>
        </nav>

        <!-- 底部：頭像 + 帳號名 + 齒輪 -->
        <div class="ur-sidebar-footer">
          <div class="ur-avatar">{{ adminInitials }}</div>
          <span class="ur-admin-name">{{ adminName }}</span>
          <button class="ur-gear-btn" @click.stop="toggleGearMenu" :title="$t('URBANRENEWAL_ADMIN.GEAR_MENU')">
            <!-- GearIcon -->
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
          </button>

          <!-- 齒輪選單（上開）：全螢幕透明遮罩負責關閉，避免 click-outside 時序問題 -->
          <div v-if="gearMenuOpen" class="ur-gear-overlay" @click="closeGearMenu"></div>
          <div v-if="gearMenuOpen" class="ur-gear-menu">
            <button class="ur-gear-menu-item" @click="goProjectSettings">
              <!-- BuildingSettingIcon -->
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              <span>{{ $t('URBANRENEWAL_ADMIN.PROJECT_SETTINGS') }}</span>
            </button>
            <button class="ur-gear-menu-item" @click="goAdminSettings">
              <!-- UserSettingIcon -->
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span>{{ $t('URBANRENEWAL_ADMIN.ADMIN_SETTINGS') }}</span>
            </button>
            <div class="ur-gear-menu-divider"></div>
            <button class="ur-gear-menu-item is-danger" @click="handleLogout">
              <!-- LogoutIcon -->
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span>{{ $t('URBANRENEWAL_ADMIN.LOGOUT') }}</span>
            </button>
          </div>
        </div>
      </aside>

      <!-- ===== Main (Topbar + Content) ===== -->
      <div class="ur-main">
        <!-- Topbar -->
        <header class="ur-topbar">
          <span class="ur-topbar-title">{{ $t('URBANRENEWAL_ADMIN.SYSTEM_TITLE') }}</span>
          <div v-if="!isSettingsPage" class="ur-topbar-right">
            <span class="ur-project-label">{{ $t('URBANRENEWAL_ADMIN.PROJECT_LABEL') }}</span>
            <div class="ur-project-select-wrap">
              <select
                class="ur-project-select"
                v-model="selectedProjectId"
              >
                <option v-if="projects.length === 0" value="" disabled>{{ $t('URBANRENEWAL_ADMIN.NO_PROJECTS') }}</option>
                <option
                  v-for="project in projects"
                  :key="project.id"
                  :value="project.id"
                >{{ project.name }}</option>
              </select>
              <!-- ChevronDown -->
              <span class="ur-project-select-chevron">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </span>
            </div>
          </div>
        </header>

        <!-- Page Content via slot -->
        <div class="ur-content">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import '@/assets/urbanrenewal/urbanrenewal-theme.css';

export default {
  name: 'UrbanRenewalAdminLayout',

  data() {
    return {
      gearMenuOpen: false,
      navItems: [
        {
          key: 'basicdata',
          path: '/urbanrenewal/admin/basicdata',
          labelKey: 'URBANRENEWAL_ADMIN.NAV_BASIC',
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>',
        },
        {
          key: 'statistics',
          path: '/urbanrenewal/admin/statistics',
          labelKey: 'URBANRENEWAL_ADMIN.NAV_STATISTICS',
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>',
        },
        {
          key: 'heatmap',
          path: '/urbanrenewal/admin/heatmap',
          labelKey: 'URBANRENEWAL_ADMIN.NAV_HEATMAP',
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>',
        },
        {
          key: 'simulation',
          path: '/urbanrenewal/admin/simulation',
          labelKey: 'URBANRENEWAL_ADMIN.NAV_SIMULATION',
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>',
        },
        {
          key: 'manualinput',
          path: '/urbanrenewal/admin/manualinput',
          labelKey: 'URBANRENEWAL_ADMIN.NAV_MANUAL',
          icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>',
        },
      ],
    };
  },

  computed: {
    ...mapState('urbanRenewalAdmin', ['projects', 'currentProjectId']),

    // 設定頁（建案設定 / 管理者設定）：頂欄右上不顯示專案選單與選配期間
    isSettingsPage() {
      const p = this.$route.path;
      return p.indexOf('projectsettings') !== -1 || p.indexOf('adminsettings') !== -1;
    },

    logoUrl() {
      return this.$store.state.urbanRenewalAdmin.logoUrl || require('@/assets/images/UPGPT.png');
    },

    // 用 v-model 綁定原生 select,確保「顯示的選項」與 currentProjectId 一致
    // (單向 :value 綁定不會讓原生 select 真的選中對應 option)。
    selectedProjectId: {
      get() {
        return this.currentProjectId;
      },
      set(id) {
        this.SET_CURRENT_PROJECT_ID(id == null ? null : Number(id));
      },
    },

    currentProjectName() {
      if (!this.currentProjectId || this.projects.length === 0) {
        return this.$t('URBANRENEWAL_ADMIN.NO_PROJECTS');
      }
      const project = this.projects.find(p => p.id === this.currentProjectId);
      return project ? project.name : '';
    },

    adminName() {
      try {
        const user = JSON.parse(window.localStorage.getItem('user') || '{}');
        return [user.last_name, user.first_name].filter(Boolean).join(' ') || 'Admin';
      } catch (_) {
        return 'Admin';
      }
    },

    adminInitials() {
      try {
        const user = JSON.parse(window.localStorage.getItem('user') || '{}');
        const last = (user.last_name || '').charAt(0);
        const first = (user.first_name || '').charAt(0);
        return (last + first) || 'A';
      } catch (_) {
        return 'A';
      }
    },
  },

  mounted() {
    this.$store.dispatch('urbanRenewalAdmin/fetchProjects');
    this.$store.dispatch('urbanRenewalAdmin/fetchLogo');
  },

  methods: {
    ...mapMutations('urbanRenewalAdmin', ['SET_CURRENT_PROJECT_ID']),

    isNavActive(path) {
      return this.$route.path === path || this.$route.path.startsWith(path + '/');
    },

    navigateTo(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },

    toggleGearMenu() {
      this.gearMenuOpen = !this.gearMenuOpen;
    },

    closeGearMenu() {
      this.gearMenuOpen = false;
    },

    goProjectSettings() {
      this.gearMenuOpen = false;
      this.$router.push('/urbanrenewal/admin/projectsettings');
    },

    goAdminSettings() {
      this.gearMenuOpen = false;
      this.$router.push('/urbanrenewal/admin/adminsettings');
    },

    handleLogout() {
      this.gearMenuOpen = false;
      // 登出重置目前建案（同時透過 mutation 清除 localStorage 記住的建案）
      this.SET_CURRENT_PROJECT_ID(null);
      // 與 CRM logoutUser() 對齊:完整清除工作階段相關 localStorage。
      // tenant 於清除前先抓,帶在登入頁網址上,方便誤登出後直接重新登入。
      const tenant = window.localStorage.getItem('tenant') || '';
      const keys = [
        'lastActive', 'idleTimeLimit',
        'access_token', 'refresh_token', 'id_token',
        'tenant', 'user', 'is_staff',
        'NoticeBoard_IsClose', 'isUpchatUser', 'isUpGPTUser',
        'upchat_login', 'sso',
      ];
      keys.forEach(k => window.localStorage.removeItem(k));
      this.$router.push({
        path: '/urbanrenewal/admin/login',
        query: tenant ? { tenant } : {},
      });
    },

  },
};
</script>
